import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { useAuthContext } from './contexts/AuthContext';
import { usePageTracking } from './hooks/usePageTracking';
import LoginPage from './pages/LoginPage';
import LeaguesPage from './pages/LeaguesPage';
import { ProfilePage } from './pages/ProfilePage';
import { CreateLeaguePage } from './pages/CreateLeaguePage';
import { LeagueDetailsPage } from './pages/LeagueDetailsPage';
import { JoinLeaguePage } from './pages/JoinLeaguePage';
import TournamentsPage from './pages/TournamentsPage';
import TournamentDetailsPage from './pages/TournamentDetailsPage';
import PublicTournamentPage from './pages/PublicTournamentPage';
import PastTournamentsPage from './pages/PastTournamentsPage';
import { CreateTournamentPage } from './pages/CreateTournamentPage';
import RosterSelectionPage from './pages/RosterSelectionPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import HowItWorksPage from './pages/HowItWorksPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import FaqPage from './pages/FaqPage';
import ContactPage from './pages/ContactPage';
import BlogListingPage from './pages/blog/BlogListingPage';
import WhatIsFantasyWrestlingArticle from './pages/blog/WhatIsFantasyWrestlingArticle';
import NcaaWrestlingScoringArticle from './pages/blog/NcaaWrestlingScoringArticle';
import DraftStrategyArticle from './pages/blog/DraftStrategyArticle';
import TournamentFormatArticle from './pages/blog/TournamentFormatArticle';
import CommissionerGuideArticle from './pages/blog/CommissionerGuideArticle';
import { AdminLayout } from './pages/admin/AdminLayout';

function App() {
  const { isAuthenticated, isAdmin, isLoading, checkAuthState } = useAuthContext();
  const navigate = useNavigate();
  usePageTracking();

  // Listen for auth events to keep state in sync
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        await checkAuthState();
        const pendingInviteCode = localStorage.getItem('pendingInviteCode');
        navigate(pendingInviteCode ? `/join/${pendingInviteCode}` : '/leagues', { replace: true });
      } else if (payload.event === 'signedOut') {
        await checkAuthState();
      }
    });
    return unsubscribe;
  }, [navigate, checkAuthState]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for pending invite code to redirect after auth
  const getPostLoginRedirect = () => {
    const pendingInviteCode = localStorage.getItem('pendingInviteCode');
    return pendingInviteCode ? `/join/${pendingInviteCode}` : '/leagues';
  };

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={getPostLoginRedirect()} replace /> : <LoginPage />}
      />
      {/* Public pages */}
      <Route path="/join/:code" element={<JoinLeaguePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/blog" element={<BlogListingPage />} />
      <Route path="/blog/what-is-fantasy-wrestling" element={<WhatIsFantasyWrestlingArticle />} />
      <Route
        path="/blog/ncaa-wrestling-scoring-explained"
        element={<NcaaWrestlingScoringArticle />}
      />
      <Route path="/blog/fantasy-wrestling-draft-strategy" element={<DraftStrategyArticle />} />
      <Route path="/blog/ncaa-wrestling-tournament-format" element={<TournamentFormatArticle />} />
      <Route
        path="/blog/how-to-run-fantasy-wrestling-league"
        element={<CommissionerGuideArticle />}
      />
      <Route
        path="/leagues"
        element={isAuthenticated ? <LeaguesPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/profile"
        element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />}
      />
      {/* Legacy route redirects */}
      <Route path="/home" element={<Navigate to="/leagues" replace />} />
      <Route path="/dashboard" element={<Navigate to="/leagues" replace />} />
      <Route
        path="/leagues/create"
        element={isAuthenticated ? <CreateLeaguePage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/leagues/:id"
        element={isAuthenticated ? <LeagueDetailsPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/leagues/:leagueId/members/:memberId/roster"
        element={isAuthenticated ? <RosterSelectionPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/tournaments"
        element={isAuthenticated ? <TournamentsPage /> : <PastTournamentsPage />}
      />
      <Route
        path="/tournaments/create"
        element={isAuthenticated ? <CreateTournamentPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/tournaments/:id"
        element={isAuthenticated ? <TournamentDetailsPage /> : <PublicTournamentPage />}
      />
      {/* Admin Console */}
      <Route
        path="/admin/*"
        element={isAuthenticated && isAdmin ? <AdminLayout /> : <Navigate to="/leagues" replace />}
      />
      <Route path="/" element={<LandingPage />} />
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900">404</h1>
              <p className="mt-2 text-gray-600">Page not found</p>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

export default App;
